import { memo, useMemo } from "react";
import styled from "@emotion/styled";
import { surface } from "@atlas-int/tokens";
import { MappedSeriesData, SeriesDataPoint } from "../types";
import type { Area } from "d3-shape";
import type { ScaleLinear } from "d3-scale";
import StackRibbon from "./StackRibbon";
import { XAxisYear, YAxis, YAxisGridlines } from "@atlas-int/viz/shared";
import { OverTimeLayoutOption } from "../constants";

const BackgroundTooltipCatcher = styled.rect`
  fill: ${surface.white};
  pointer-events: all;
`;

const FullWidthAndHeight = styled.rect`
  fill: none;
  stroke: none;
  pointer-events: none;
`;

interface RibbonLayerProps {
  mappedSeriesData: MappedSeriesData[];
  stackArea: Area<SeriesDataPoint>;
  useColorMap: Map<string, string>;
  onRibbonMouseEnter: (data: {
    seriesKey: string;
    series: SeriesDataPoint[];
    fillColor: string;
  }) => void;
  onRibbonMouseLeave: () => void;
}

// Memoized component that only re-renders when series data changes
const RibbonLayer = memo(
  ({
    mappedSeriesData,
    stackArea,
    useColorMap,
    onRibbonMouseEnter,
    onRibbonMouseLeave,
  }: RibbonLayerProps) => {
    // Reverse once and memoize
    const reversedSeriesData = useMemo(() => {
      return [...mappedSeriesData].reverse();
    }, [mappedSeriesData]);

    return (
      <>
        {reversedSeriesData.map(({ seriesKey, mappedSeries, useId }) => (
          <StackRibbon
            key={seriesKey}
            series={mappedSeries}
            seriesKey={seriesKey}
            areaGenerator={stackArea}
            useId={useId}
            colorMap={useColorMap}
            onMouseEnter={onRibbonMouseEnter}
            onMouseLeave={onRibbonMouseLeave}
          />
        ))}
      </>
    );
  },
);

interface ChartSVGProps {
  chartWidth: number;
  chartHeight: number;
  handleChartMouseMove: (e: React.MouseEvent<SVGElement>) => void;
  clearTooltip: () => void;
  svgRef: React.RefObject<SVGSVGElement>;
  yScale: ScaleLinear<number, number>;
  xScale: ScaleLinear<number, number>;
  containerSize: { width: number; height: number };
  chartMargin: { left: number; right: number; top: number; bottom: number };
  mappedSeriesData: MappedSeriesData[];
  stackArea: Area<SeriesDataPoint>;
  useColorMap: Map<string, string>;
  onRibbonMouseEnter: (data: {
    seriesKey: string;
    series: SeriesDataPoint[];
    fillColor: string;
  }) => void;
  onRibbonMouseLeave: () => void;
  overTimeLayout: OverTimeLayoutOption;
}

const ChartSVG = ({
  chartWidth,
  chartHeight,
  handleChartMouseMove,
  clearTooltip,
  svgRef,
  yScale,
  xScale,
  containerSize,
  chartMargin,
  mappedSeriesData,
  stackArea,
  useColorMap,
  onRibbonMouseEnter,
  onRibbonMouseLeave,
  overTimeLayout,
}: ChartSVGProps) => {
  return (
    <svg
      role="img"
      aria-label="Trade over time chart"
      style={{ backgroundColor: surface.white }}
      width={chartWidth}
      height={chartHeight}
      onMouseMoveCapture={handleChartMouseMove}
      ref={svgRef}
    >
      <BackgroundTooltipCatcher
        x={0}
        y={0}
        width={chartWidth}
        height={chartHeight}
        onMouseMove={clearTooltip}
        fill="none"
      />

      <YAxisGridlines
        scale={yScale}
        containerSize={containerSize}
        chartMargin={chartMargin}
      />
      <g>
        <FullWidthAndHeight
          x={0}
          y={0}
          width={chartWidth}
          height={chartHeight}
          fill="none"
        />
        <RibbonLayer
          mappedSeriesData={mappedSeriesData}
          stackArea={stackArea}
          useColorMap={useColorMap}
          onRibbonMouseEnter={onRibbonMouseEnter}
          onRibbonMouseLeave={onRibbonMouseLeave}
        />
      </g>

      <XAxisYear
        scale={xScale}
        containerSize={{ width: chartWidth, height: chartHeight }}
        chartMargin={chartMargin}
      />
      <YAxis
        scale={yScale}
        overTimeLayout={overTimeLayout}
        chartMargin={chartMargin}
      />
    </svg>
  );
};

// Memo ChartSVG to prevent re-renders when parent updates
export default memo(ChartSVG);
